import type { DeviceSearchFilters, DeviceSearchValue } from '@/api/deviceApi'
import { type StateKey, isDerivedStateKey } from '@/api/stateKeysApi'
import type { Stream } from '@/api/streamsApi'
import type { Condition } from '@/config/utils'
import { DEFAULT_SERIAL_KEY_LABEL } from '@/stores/auth'
import {
	type ClickhouseToTSTypes,
	getTypeFromClickhouseToTS,
	isDateTimeType,
	isNumericFieldType,
} from '@/utils/clickhouseTypes'
import { derivedStateFilter } from '@/utils/derivedStateFilter'
import { getDeviceStateFieldInfo } from '@/utils/deviceFieldUtils'
import { escapeRegexLiteral } from '@/utils/normalization'
import type { FilterKey, FilterState } from './type'

export const HeartbeatFilterOptions = [
	{
		key: 'few_seconds',
		text: 'few seconds',
		value: 60, // 60 seconds
	},
	{
		key: '5_minutes',
		text: '5 minutes',
		value: 5 * 60, // 5 minutes in seconds
	},
	{
		key: '1_hour',
		text: '1 hour',
		value: 60 * 60, // 1 hour in seconds
	},
	{
		key: '1_day',
		text: '1 day',
		value: 24 * 60 * 60, // 1 day in seconds
	},
	{
		key: '1_week',
		text: '1 week',
		value: 7 * 24 * 60 * 60, // 1 week in seconds
	},
	{
		key: '1_month',
		text: '1 month',
		value: 30 * 24 * 60 * 60, // 30 days in seconds
	},
	{
		key: '1_year',
		text: '1 year',
		value: 365 * 24 * 60 * 60, // 365 days in seconds
	},
	{
		key: 'custom',
		text: 'Custom',
		value: null, // Will be set based on user input
		isCustom: true,
	},
]

// Time unit options for custom time selection
export const TimeUnitOptions = [
	{ key: 'seconds', text: 'Seconds', value: 'seconds' },
	{ key: 'minutes', text: 'Minutes', value: 'minutes' },
	{ key: 'hours', text: 'Hours', value: 'hours' },
	{ key: 'days', text: 'Days', value: 'days' },
	{ key: 'weeks', text: 'Weeks', value: 'weeks' },
	{ key: 'months', text: 'Months', value: 'months' },
	{ key: 'years', text: 'Years', value: 'years' },
]

export const HeartbeatField = 'last_heartbeat'

export type MetadataFilterValueType = string | null
export type SearchDevicesFiltersType = {
	metaDatafilters?: Record<string, MetadataFilterValueType | MetadataFilterValueType[]>
	stateFilters?: Condition[]
}

export type Filters = {
	[key: string]: MetadataFilterValueType[]
}

export type QueryCondition = {
	column: string
	operator: string
	isNullOperator: boolean
	columnType: ClickhouseToTSTypes
	value: unknown
}

/**
 * Converts an object to a formatted query array.
 * @param query - The object representing the query.
 * @returns The formatted query array.
 * @example return [ "and", ["=" ,  { "column": "id"}, {"value": 2} ], ["not=", { "column": "soc"}, {"value": 2}] ];
 */

export function objectToQuery(query: QueryCondition[]) {
	const formattedQuery: (string | [string, { column: string }, { value: unknown }])[] = []

	formattedQuery.push('and')

	for (const condition of query) {
		// converting value to number
		const { columnType, isNullOperator, operator, column } = condition
		let { value } = condition

		if (typeof value === 'string') {
			if (isNullOperator) {
				value = null
			}
			// Check if the string can be parsed to a boolean, when the column type is given as boolean or if it's not present do the conversion
			else if (!columnType || columnType === 'boolean') {
				if (value?.toLowerCase() === 'true') {
					value = true
				} else if (value?.toLowerCase() === 'false') {
					value = false
				}
			}
			// Check if the string can be parsed to a float
			else if (/^[-+]?\d*\.?\d+([eE][-+]?\d+)?$/.test(value) && columnType !== 'string') {
				const parsedValue = Number.parseFloat(value)
				if (!Number.isNaN(parsedValue)) {
					value = parsedValue
				}
			}
		}

		formattedQuery.push([operator, { column }, { value }])
	}
	return formattedQuery as unknown as Condition[]
}

const convertCustomTimeToSeconds = (value: number, unit: string): number => {
	const conversions = {
		seconds: 1,
		minutes: 60,
		hours: 60 * 60,
		days: 24 * 60 * 60,
		weeks: 7 * 24 * 60 * 60,
		months: 30 * 24 * 60 * 60,
		years: 365 * 24 * 60 * 60,
	}

	return value * (conversions[unit as keyof typeof conversions] || 1)
}

export const stateFiltersToQuery = (filters: FilterState[]): SearchDevicesFiltersType['stateFilters'] => {
	let stateFilters: SearchDevicesFiltersType['stateFilters'] = []

	const regularStateFilters: FilterState[] = filters.filter(
		f => f.type === 'state' && f.field && (f.value || f.isNullOperator) && f.field !== HeartbeatField,
	)

	// Getting heartbeat filters separately
	const heartbeatFilters: FilterState[] = filters.filter(
		f => f.type === 'state' && f.field === HeartbeatField && (f.value || (f.customTimeValue && f.customTimeUnit)),
	)

	// both regular and heartbeat filters
	const allStateFilters: FilterState[] = [...regularStateFilters]

	// Convert heartbeat filters to heartbeat state filters
	for (const heartbeatFilter of heartbeatFilters) {
		let timeValueInSeconds: number

		// Handling custom time input
		if (heartbeatFilter.value === 'custom' && heartbeatFilter.customTimeValue && heartbeatFilter.customTimeUnit) {
			timeValueInSeconds = convertCustomTimeToSeconds(heartbeatFilter.customTimeValue, heartbeatFilter.customTimeUnit)
		} else {
			// Handling predefined options
			const option = HeartbeatFilterOptions.find(opt => opt.key === heartbeatFilter.value)
			if (!option) {
				continue // Skip if option not found
			}
			timeValueInSeconds = option.value ?? 0 // Default to 0 if option.value is null
		}

		const currentTimeInSeconds = Math.floor(Date.now() / 1000)
		const timestampValue = currentTimeInSeconds - timeValueInSeconds

		// Add the converted heartbeat filter as a heartbeat state filter
		allStateFilters.push({
			id: heartbeatFilter.id,
			field: 'heartbeat',
			columnType: 'int',
			type: 'state',
			operator: '>',
			isNullOperator: false,
			value: String(timestampValue),
		})
	}

	if (allStateFilters.length > 0) {
		// Group state filters by field, operator and value.
		const stateQuery = allStateFilters.map(f => {
			let finalValue: unknown

			if (f.isNullOperator) {
				finalValue = f.operator === 'is_null' ? null : ''
			} else {
				const strVal = String(f.value)
				// ✅ Convert "true"/"false" strings to actual booleans for state filters
				if (strVal.toLowerCase() === 'true') {
					finalValue = true
				} else if (strVal.toLowerCase() === 'false') {
					finalValue = false
				} else {
					finalValue = strVal
				}
			}

			return {
				column: f.field,
				operator: f.operator,
				isNullOperator: f.isNullOperator,
				columnType: f.columnType,
				value: finalValue,
			}
		})
		stateFilters = objectToQuery(stateQuery)
	}

	return stateFilters
}

/**
 * Coerce a raw string value into the appropriate DeviceSearch primitive type.
 * Handles booleans, numbers, and timestamps for state fields.
 */
const coerceDeviceSearchValue = (
	value: string | number | null,
	columnType?: ClickhouseToTSTypes,
	fieldName?: string,
	isState?: boolean,
): DeviceSearchValue => {
	if (value === null) {
		return ''
	}
	const strVal = String(value)

	const isTimestampField =
		fieldName === 'heartbeat' ||
		fieldName?.endsWith('heartbeat') ||
		fieldName?.includes('timestamp') ||
		fieldName?.includes('updated_at') ||
		(columnType &&
			(isDateTimeType(columnType) ||
				columnType.toLowerCase().includes('date') ||
				columnType.toLowerCase().includes('time') ||
				columnType.toLowerCase().includes('timestamp')))

	// Timestamp fields: wrap in { timestamp: ... } with ISO format
	if (isTimestampField) {
		const num = Number(strVal)
		if (!Number.isNaN(num)) {
			// If it's a duration offset in milliseconds (like 3600000 or 86400000), convert to absolute ISO timestamp in past
			if (num < 100000000000) {
				return { timestamp: new Date(Date.now() - num).toISOString() }
			}
			// If it's epoch milliseconds, convert to ISO
			if (num > 1000000000000) {
				return { timestamp: new Date(num).toISOString() }
			}
			// If it's epoch seconds, convert to ISO
			if (num > 1000000000) {
				return { timestamp: new Date(num * 1000).toISOString() }
			}
		}
		// Already ISO or date string
		return { timestamp: strVal }
	}

	// Boolean coercion — state columns only. State bool columns are `boolean` in the backend and require
	// a real Bool. Metadata/id columns are `text`; a JS boolean is rejected there (`invalid filter for
	// column X`), so a value literally "true"/"false" must stay a string.
	if (isState) {
		if (strVal.toLowerCase() === 'true') {
			return true
		}
		if (strVal.toLowerCase() === 'false') {
			return false
		}
	}

	// Numeric coercion for state columns or when columnType is numeric
	const isNumericType =
		columnType === 'int' ||
		columnType === 'uint' ||
		columnType === 'float' ||
		(isState && /^[-+]?\d*\.?\d+([eE][-+]?\d+)?$/.test(strVal))

	if (isNumericType) {
		const num = Number(strVal)
		if (!Number.isNaN(num)) {
			return num
		}
	}

	// Try numeric coercion for values that look numeric (unless explicitly string type)
	if (columnType !== 'string' && /^[-+]?\d*\.?\d+([eE][-+]?\d+)?$/.test(strVal)) {
		const num = Number(strVal)
		if (!Number.isNaN(num)) {
			return num
		}
	}

	return strVal
}

// Backend caps regex patterns at 64 bytes (incl. the `(?i)` prefix). Measure bytes, not UTF-16 units.
const MAX_REGEX_PATTERN_BYTES = 64
const REGEX_CASE_INSENSITIVE_PREFIX = '(?i)'

const byteLength = (value: string): number => new TextEncoder().encode(value).length

// Build a case-insensitive `(?i)` regex for ONE substring value, bounded to the 64-byte cap. A too-long
// value is trimmed on a char boundary (never mid-escape). Multiple values are emitted as separate OR'd
// nodes (see filterStateToDeviceSearch), so nothing is silently dropped from a combined pattern.
const toBoundedRegexPattern = (value: string): string => {
	const budget = MAX_REGEX_PATTERN_BYTES - byteLength(REGEX_CASE_INSENSITIVE_PREFIX)
	let raw = value
	while (raw.length > 0 && byteLength(escapeRegexLiteral(raw)) > budget) {
		raw = raw.slice(0, -1)
	}
	return `${REGEX_CASE_INSENSITIVE_PREFIX}${escapeRegexLiteral(raw)}`
}

// Char budget for a substring value (`(?i)` takes 4 bytes). Ceiling for plain text; metachars count double.
export const MAX_SUBSTRING_VALUE_CHARS = MAX_REGEX_PATTERN_BYTES - REGEX_CASE_INSENSITIVE_PREFIX.length

// Error message if a substring value exceeds the 64-byte cap once escaped + `(?i)`-prefixed, else null.
export const validateSubstringValueLength = (value: string): string | null => {
	const pattern = `${REGEX_CASE_INSENSITIVE_PREFIX}${escapeRegexLiteral(value)}`
	if (byteLength(pattern) > MAX_REGEX_PATTERN_BYTES) {
		return `Search term too long (max ${MAX_SUBSTRING_VALUE_CHARS} characters)`
	}
	return null
}

/** Negating operators, for which a derived column's expansion is wrapped in `not`. */
const NEGATING_OPERATORS = new Set(['not=', 'ne', '!=', 'is_not', 'not_in', 'notEquals'])

/**
 * Convert a single FilterState into a DeviceSearchFilters object.
 */
const filterStateToDeviceSearch = (filter: FilterState): DeviceSearchFilters | null => {
	// A derived column has no backend column — send the conditions its selected states match on.
	if (filter.derivedExpression) {
		const values = (Array.isArray(filter.value) ? filter.value : [filter.value])
			.filter(value => value !== null && value !== undefined)
			.map(String)
		const node = derivedStateFilter(
			filter.derivedExpression,
			values,
			filter.derivedStateColumns ? new Set(filter.derivedStateColumns) : undefined,
		)
		if (!node) {
			return null
		}
		return NEGATING_OPERATORS.has(filter.operator) || NEGATING_OPERATORS.has(filter.operatorLabel ?? '')
			? { not: node }
			: node
	}
	const { field, operator, operatorLabel, isNullOperator, columnType, type } = filter

	// Determine which condition key to use based on operator
	const getConditionKey = (): string => {
		const op = operatorLabel || operator
		// Map UI operator labels to search API condition keys
		switch (op) {
			case '=':
			case 'equals':
			case 'is':
				return 'eq'
			case 'not=':
			case 'notEquals':
			case 'is_not':
				return 'ne'
			case '>':
			case 'timeRange':
				return 'gt'
			case '<':
				return 'lt'
			case '>=':
				return 'gte'
			case '<=':
				return 'lte'
			case 'is_null':
				return 'is_null'
			case 'is_not_null':
				return 'is_not_null'
			case 'contains':
			case 'regex':
				return 'regex'
			default:
				return 'eq'
		}
	}

	const conditionKey = getConditionKey()

	let conditionValue: Record<string, unknown> = {}
	// Set when a not-equals filter holds multiple values: the API has no "nin", so wrap in `not`.
	let negateNode = false

	if (isNullOperator || conditionKey === 'is_null' || conditionKey === 'is_not_null') {
		const isNull =
			conditionKey === 'is_null' ||
			(isNullOperator && (operatorLabel === 'is_null' || operator === 'is_null' || operator === '='))
		conditionValue = { is_null: isNull }
	} else {
		const values = Array.isArray(filter.value) ? filter.value : [filter.value]
		// Drop null/undefined and empty/whitespace-only values so a blank entry never reaches the
		// payload (e.g. `in: ["", "off"]`). Targeting missing values is done via is_null/is_not_null.
		const filteredValues = values.filter(v => v !== null && v !== undefined && String(v).trim() !== '')

		if (filteredValues.length === 0) {
			return null
		}

		const isState = type === 'state' || field.startsWith('state.')

		if (conditionKey === 'regex') {
			// Case-insensitive substring search. Each value becomes its own bounded `(?i)` regex node; multiple
			// values are OR'd. This avoids a single combined regex silently dropping values past the 64-byte cap.
			const patterns = filteredValues.map(v => toBoundedRegexPattern(String(v)))
			if (patterns.length > 1) {
				return orAll(patterns.map(pattern => buildLeaf({ regex: pattern })))
			}
			conditionValue = { regex: patterns[0] }
		} else if (filteredValues.length > 1) {
			// Multiple values → use "in". For not-equals there's no "nin" in the API, so the caller
			// negates the whole node below (NOT (column IN [...])).
			const coercedValues = filteredValues.map(v =>
				coerceDeviceSearchValue(v as string | number, columnType, field, isState),
			)
			conditionValue = { in: coercedValues }
			negateNode = conditionKey === 'ne'
		} else {
			// Single value → use the determined operator
			const coercedValue = coerceDeviceSearchValue(filteredValues[0] as string | number, columnType, field, isState)
			conditionValue = { [conditionKey]: coercedValue }
		}
	}

	return negateNode ? { not: buildLeaf(conditionValue) } : buildLeaf(conditionValue)

	// Route a condition value to the correct filter node based on field source. Defined last (hoisted) so
	// both the single-node path above and the multi-value regex OR path can reuse it.
	function buildLeaf(cond: Record<string, unknown>): DeviceSearchFilters {
		if (field === 'id') {
			return { id: cond }
		}
		// Org-level project filter. Values must already be project ids (= tenant_id); the caller maps the
		// selected project names → ids before calling this.
		if (field === 'tenantName' || field === 'project') {
			return { project_id: cond }
		}
		// Org-level uid filter (bigint). Caller coerces values to numbers before calling this.
		if (field === 'uid') {
			return { uid: cond }
		}
		if (type === 'state' || field.startsWith('state.')) {
			const stateColumnName = field.startsWith('state.') ? field.replace('state.', '') : field
			return { state: { column: stateColumnName, ...cond } }
		}
		// Default to metadata (type === 'metadata' or field starts with 'metadata.')
		const columnName = field.startsWith('metadata.') ? field.replace('metadata.', '') : field
		return { metadata: { column: columnName, ...cond } }
	}
}

/**
 * Converts FilterState[] from the search bar into DeviceSearch recursive filter format.
 * This is the replacement of buildSearchQuery.
 *
 * @param filters - Array of FilterState from the filterable search bar
 * @returns DeviceSearchFilters object ready to be sent to POST /v3/devices/search
 */
export const buildDeviceSearchFilters = (filters: FilterState[]): DeviceSearchFilters | null => {
	if (!filters || filters.length === 0) {
		return null
	}

	// Separate heartbeat filters and convert them to heartbeat state filters
	const processedFilters: FilterState[] = []

	for (const filter of filters) {
		if (filter.field === HeartbeatField) {
			// Convert heartbeat filter to heartbeat state field
			let timeValueInSeconds: number

			if (filter.value === 'custom' && filter.customTimeValue && filter.customTimeUnit) {
				timeValueInSeconds = convertCustomTimeToSeconds(filter.customTimeValue, filter.customTimeUnit)
			} else {
				const option = HeartbeatFilterOptions.find(opt => opt.key === String(filter.value))
				if (!option || option.value === null) {
					continue
				}
				timeValueInSeconds = option.value
			}

			const currentTimeInSeconds = Math.floor(Date.now() / 1000)
			const timestampValue = currentTimeInSeconds - timeValueInSeconds

			processedFilters.push({
				...filter,
				field: 'heartbeat',
				type: 'state',
				operator: '>',
				operatorLabel: '>',
				isNullOperator: false,
				value: String(timestampValue),
				columnType: 'int',
			})
		} else {
			processedFilters.push(filter)
		}
	}

	const searchFilterParts: DeviceSearchFilters[] = processedFilters
		.map(f => filterStateToDeviceSearch(f))
		.filter((f): f is DeviceSearchFilters => f !== null)

	if (searchFilterParts.length === 0) {
		return null
	}

	// Combine with AND, nested so no node exceeds the backend's 16-arg cap.
	return andAll(searchFilterParts)
}

// Backend rejects an AND/OR node with more than 16 args.
const MAX_FILTER_GROUP_SIZE = 16

// AND the parts together, nesting into groups of ≤16 so no single `and` node trips the backend's arg
// cap. AND is associative, so grouping doesn't change the result.
// NOTE: assumes a pure-AND list — if OR is ever mixed in here, nesting would change the logic.
export const andAll = (parts: DeviceSearchFilters[]): DeviceSearchFilters => {
	if (parts.length === 1) {
		return parts[0]
	}
	if (parts.length <= MAX_FILTER_GROUP_SIZE) {
		return { and: parts }
	}
	const groups: DeviceSearchFilters[] = []
	for (let i = 0; i < parts.length; i += MAX_FILTER_GROUP_SIZE) {
		groups.push({ and: parts.slice(i, i + MAX_FILTER_GROUP_SIZE) })
	}
	// Recurse in case there are more than 16 groups.
	return andAll(groups)
}

// OR the parts together, nested into groups of ≤16 (same arg cap as AND). OR is associative, so grouping
// doesn't change the result. Used for multi-value substring search (one regex node per value) and for
// cross-column free-text search (device_state View Data).
export const orAll = (parts: DeviceSearchFilters[]): DeviceSearchFilters => {
	if (parts.length === 1) {
		return parts[0]
	}
	if (parts.length <= MAX_FILTER_GROUP_SIZE) {
		return { or: parts }
	}
	const groups: DeviceSearchFilters[] = []
	for (let i = 0; i < parts.length; i += MAX_FILTER_GROUP_SIZE) {
		groups.push({ or: parts.slice(i, i + MAX_FILTER_GROUP_SIZE) })
	}
	return orAll(groups)
}

/**
 * Get the fields options for filters based on metadata keys, state keys, and stream details.
 * Returns FilterKey[] format for the new UI components.
 */
export interface GetFieldsOptionsConfig {
	includeDeviceId?: boolean
	includeMetadata?: boolean
	includeAllStateFields?: boolean
	excludeDerivedStateKeys?: boolean
}

export const getFieldsOptions = (
	stateFieldIds: string[],
	metadataKeys: string[],
	streams?: Stream[],
	stateKeys: StateKey[] = [],
	serialKeyLabel = DEFAULT_SERIAL_KEY_LABEL,
	options?: GetFieldsOptionsConfig,
): FilterKey[] => {
	const keys: FilterKey[] = []
	const streamFieldTypes = new Map<string, string>()
	for (const stream of streams ?? []) {
		for (const field of stream.fields) {
			streamFieldTypes.set(`${stream.name}.${field.name}`, field.type)
		}
	}

	if (options?.includeDeviceId !== false) {
		// Serial Key / Device ID (always first unless disabled)
		keys.push({
			id: 'id',
			label: serialKeyLabel,
			type: 'string',
			skipOperator: true,
			source: 'system',
		})
	}

	// Metadata fields
	if (options?.includeMetadata !== false && metadataKeys.length > 0) {
		const metadataOptions = metadataKeys
			.filter(key => key !== 'id') // Exclude id as it's already added
			.map(key => ({
				id: key,
				label: key.charAt(0).toUpperCase() + key.slice(1),
				type: 'string' as const,
				source: 'metadata' as const,
			}))

		keys.push(...metadataOptions)
	}

	keys.push({
		id: 'heartbeat',
		label: 'Heartbeat',
		type: 'string',
		source: 'deviceState',
		clickhouseType: 'DateTime64(3)',
	})

	if (stateFieldIds.length > 0) {
		const stateOptions = stateFieldIds
			.filter(key => {
				if (key.startsWith('.')) {
					return false
				}
				if (options?.excludeDerivedStateKeys) {
					const stateKey = stateKeys.find(sk => sk.display_name === key)
					if (stateKey && isDerivedStateKey(stateKey)) {
						return false
					}
				}
				return true
			})
			.map(key => {
				const clickhouseType = getDeviceStateFieldInfo(streams, stateKeys, key)?.type
				const tsType = clickhouseType ? getTypeFromClickhouseToTS(clickhouseType) : 'string'
				const filterType: 'string' | 'number' | 'boolean' = isNumericFieldType(clickhouseType)
					? 'number'
					: tsType === 'boolean'
						? 'boolean'
						: 'string'
				// A derived column can only ever hold one of its own states, so it carries its value set
				// (and each state's badge look) instead of asking the backend for distinct values.
				const stateKey = stateKeys.find(sk => sk.display_name === key)
				const derivedOptions =
					stateKey && isDerivedStateKey(stateKey)
						? stateKey.expression.map(state => ({
								value: state.name,
								label: state.name,
								icon: state.icon,
								color: state.color,
							}))
						: undefined
				return {
					id: key,
					label: key.charAt(0).toUpperCase() + key.slice(1),
					type: filterType,
					source: 'deviceState' as FilterKey['source'],
					clickhouseType,
					options: derivedOptions,
				}
			})
			.filter(field => {
				if (field.id === 'heartbeat' || options?.includeAllStateFields) {
					return true
				}
				return getTypeFromClickhouseToTS(field.clickhouseType ?? '') === 'string'
			})

		keys.push(...stateOptions)
	}

	return keys
}

export const NULL_OPERATORS = ['is_null', 'is_not_null']

export const combineFilters = (newFilter: FilterState, existingFilters: FilterState[]): FilterState[] => {
	const existingFilterIndex = existingFilters.findIndex(
		filter => filter.field === newFilter.field && filter.operator === newFilter.operator,
	)

	if (existingFilterIndex !== -1) {
		const isSingleSelectionField = newFilter.field === HeartbeatField
		if (isSingleSelectionField) {
			return [
				...existingFilters.slice(0, existingFilterIndex),
				newFilter,
				...existingFilters.slice(existingFilterIndex + 1),
			]
		}

		const existingFilter = existingFilters[existingFilterIndex]
		const combinedValues = Array.isArray(existingFilter.value)
			? [...existingFilter.value, ...(Array.isArray(newFilter.value) ? newFilter.value : [newFilter.value])]
			: [existingFilter.value, ...(Array.isArray(newFilter.value) ? newFilter.value : [newFilter.value])]

		const updatedFilter: FilterState = {
			...existingFilter,
			value: combinedValues,
			valueLabels: [...(existingFilter.valueLabels || []), ...(newFilter.valueLabels || [])],
		}

		return [
			...existingFilters.slice(0, existingFilterIndex),
			updatedFilter,
			...existingFilters.slice(existingFilterIndex + 1),
		]
	}

	return [...existingFilters, newFilter]
}

export const formatFilterDisplay = (filter: FilterState): string => {
	const values = Array.isArray(filter.value) ? filter.value : [filter.value]
	let valueLabels = values.map(v => String(v)).filter(Boolean)

	if (filter.isNullOperator) {
		return `${filter.fieldLabel} ${filter.operatorLabel}`
	}

	if (filter.field === HeartbeatField && filter.value === 'custom' && filter.customTimeValue && filter.customTimeUnit) {
		valueLabels = [`${filter.customTimeValue} ${filter.customTimeUnit}`]
	}

	const displayOperator = filter.field === HeartbeatField && filter.operator === 'equals' ? 'in last' : filter.operator

	return valueLabels.length > 1
		? `${filter.fieldLabel} ${displayOperator} ${valueLabels.join(' or ')}`
		: `${filter.fieldLabel} ${displayOperator} ${valueLabels[0] || ''}`
}
